import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';
import LongTextEllipsis from '../LongTextEllipsis';
import BasicBlock from '../components/BasicBlock';
import TypeIcon from '../TypeIcon';
import {
  formatTimestamp,
  defaultEmployeeString,
  defaultAutomationString,
} from '../helpers';
import { TeamMemberBlock } from '../components/TeamMemberBlock';
import { CardHeader } from '../components/CardHeader';
import { initiatorTypes } from '../../constants';
import { RoleBlock } from '../components/RoleBlock';
import { HtmlContainer } from '../FieldsTable';
import { AssociationsBlock } from '../components/AssosiationBlock';

const cardHeaderConfig = (t) => {
  const prefix = t('scheduled activity deleted by');
  return {
    [initiatorTypes.employee]: {
      prefix,
      creationType: defaultEmployeeString,
    },
    [initiatorTypes.employee_api_request]: {
      prefix,
      creationType: defaultEmployeeString,
      postfix: t('via API'),
    },
    [initiatorTypes.automation]: {
      prefix,
      creationType: defaultAutomationString,
    },
  };
};

const ScheduledActivityDeletedEventBlock = ({ event, business, ...others }) => {
  const { t } = useTranslation();

  const dueDate = useMemo(() => {
    const date = new Date(event.data.dueDatetime).toLocaleString('en-US');
    return formatTimestamp(date);
  }, [event]);

  return (
    <BasicBlock
      comments={event.comments}
      icon={
        <TypeIcon
          color="var(--color-viz-cat-5-primary)"
          icon="clipboard-list"
          size="16px"
        />
      }
      isDeletable={false}
      timestamp={event.created}
      title={
        <CardHeader
          event={event}
          name={event.data.activityName}
          config={cardHeaderConfig}
        />
      }
      event={event}
      {...others}
    >
      <TextEllipsisWithTooltip weight="bold">
        {t('Due Date (My Time)')}
      </TextEllipsisWithTooltip>
      <LongTextEllipsis data-qa-field="due-date">{dueDate}</LongTextEllipsis>
      <TextEllipsisWithTooltip weight="bold">
        {t('Assignee')}
      </TextEllipsisWithTooltip>
      {event.data.employee ? (
        <TeamMemberBlock
          item={{ value: event.data.employee, type: 'selector' }}
        />
      ) : (
        <RoleBlock item={{ value: event.data.role }} />
      )}
      {event.data?.associations ? (
        <>
          <TextEllipsisWithTooltip weight="bold">
            {t('Associations')}
          </TextEllipsisWithTooltip>
          <AssociationsBlock
            data-qa-field="associations"
            associations={event.data.associations}
          />
        </>
      ) : null}
      {event.data?.note ? (
        <>
          <TextEllipsisWithTooltip weight="bold">
            {t('Notes')}
          </TextEllipsisWithTooltip>
          <HtmlContainer
            data-qa-field="note"
            isExpandable
            dangerouslySetInnerHTML={{ __html: event.data.note }}
            className="note"
          />
        </>
      ) : null}
    </BasicBlock>
  );
};

export default ScheduledActivityDeletedEventBlock;
